import { usePharmaCompanyOptions } from '@/entities/filter/hooks/useFilterOptions';
import { cn } from '@/shared/lib/utils';
import { Checkbox, Icon, Spinner } from '@/shared/ui';
import { useId, useRef, useState } from 'react';

interface PharmaCompanyMultiSelectProps {
  value: number[];
  onChange: (ids: number[]) => void;
}

/**
 * Figma Select/제약사 — 고른 제약사 칩(× 로 빼기) + 검색 입력 + 체크 목록.
 * 선택지는 /partner/filters/pharma-companies (거래처 정보 거래의 제약사).
 * 모달 안에서 쓰므로 목록은 포털 없이 필드 아래에 띄운다(포털 레이어는 모달 밖이라 눌리지 않는다).
 */
export function PharmaCompanyMultiSelect({
  value,
  onChange,
}: PharmaCompanyMultiSelectProps) {
  const { data = [], isPending } = usePharmaCompanyOptions();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const keyword = query.trim();
  const options = keyword
    ? data.filter((company) => company.pharmaCompanyName?.includes(keyword))
    : data;
  const selected = value
    .map((id) => data.find((company) => company.pharmaCompanyId === id))
    .filter((company) => company !== undefined);

  const toggle = (id: number, checked: boolean) =>
    onChange(checked ? [...value, id] : value.filter((item) => item !== id));

  return (
    <div
      ref={rootRef}
      className="relative w-full"
      // 필드·목록 밖으로 포커스가 나가면 닫는다
      onBlur={(event) => {
        if (!rootRef.current?.contains(event.relatedTarget as Node | null))
          setOpen(false);
      }}
      onKeyDown={(event) => {
        // 목록만 닫고 모달은 닫지 않는다
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <div
        // 칩 사이 빈 곳을 눌러도 검색 입력으로
        onClick={() => {
          inputRef.current?.focus();
          setOpen(true);
        }}
        className={cn(
          'flex min-h-12 w-full cursor-text flex-wrap items-center gap-1.5 rounded-12 border-[1.5px] border-line-grey bg-surface-white px-3 py-2',
          open && 'border-fg-accent',
        )}
      >
        {selected.map((company) => (
          <span
            key={company.pharmaCompanyId}
            className="flex items-center gap-0.5 rounded-full bg-primary-50 py-1 pr-1.5 pl-2.5 text-body-13 font-medium text-fg-accent"
          >
            {company.pharmaCompanyName}
            <button
              type="button"
              aria-label={`${company.pharmaCompanyName} 빼기`}
              className="flex size-4 cursor-pointer items-center justify-center rounded-full hover:bg-primary-100"
              onClick={(event) => {
                event.stopPropagation();
                toggle(company.pharmaCompanyId, false);
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          role="combobox"
          aria-label="제약사 검색"
          aria-expanded={open}
          aria-controls={listId}
          value={query}
          placeholder="제약사 검색"
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            // 검색어가 없을 때 지우기 = 마지막 칩 빼기
            if (event.key === 'Backspace' && !query && value.length > 0)
              onChange(value.slice(0, -1));
          }}
          className="min-w-[96px] flex-1 bg-transparent text-body-14 font-regular text-fg-primary outline-none placeholder:text-fg-light"
        />
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={24} />
      </div>
      {open && (
        <div
          id={listId}
          role="group"
          aria-label="제약사 목록"
          // 제약사 이름(라벨)을 눌러도 포커스가 검색 입력에 남게 — 포커스가 빠지면 목록이 닫힌다
          onMouseDown={(event) => event.preventDefault()}
          className="absolute inset-x-0 top-full z-10 mt-2 flex max-h-[240px] flex-col gap-3 overflow-y-auto rounded-20 bg-surface-white p-4 shadow-popover"
        >
          {isPending ? (
            <div className="flex items-center gap-2 text-body-14 font-medium text-fg-light">
              <Spinner size="sm" />
              불러오는 중…
            </div>
          ) : options.length === 0 ? (
            <p className="text-body-14 font-medium text-fg-light">
              {keyword
                ? '검색 결과가 없어요.'
                : '고를 수 있는 제약사가 없어요.'}
            </p>
          ) : (
            options.map((company) => (
              <Checkbox
                key={company.pharmaCompanyId}
                label={company.pharmaCompanyName ?? '-'}
                checked={value.includes(company.pharmaCompanyId)}
                onCheckedChange={(checked) =>
                  toggle(company.pharmaCompanyId, checked)
                }
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
